/**
 * Which lookups a query runs. A dialable number goes to the resolver (one account, a chain, a
 * recognised caller); eleven digits starting with 1 could be a phone or a MID, so both run; an
 * eleven-digit Maverick MID (3011..., 5011...) is never a phone and never reaches the resolver,
 * which would only call it unparseable; partial numbers and names search the table.
 */
import { classifyQuery, type QueryClass } from '@lsc/domain';

export interface LookupPlan {
  resolving: boolean;
  searching: boolean;
  kind: QueryClass['kind'];
}

export function lookupPlan(q: string): LookupPlan {
  const cls = classifyQuery(q);
  switch (cls.kind) {
    case 'empty':
      return { resolving: false, searching: false, kind: cls.kind };
    case 'phone':
      return { resolving: cls.dialable, searching: !cls.dialable, kind: cls.kind };
    case 'ambiguous':
      return { resolving: cls.digits.startsWith('1'), searching: true, kind: cls.kind };
    case 'account':
      return { resolving: false, searching: true, kind: cls.kind };
  }
}

/** Table rows the resolver already lists are dropped, so one account never renders (or gets an id) twice. */
export function mergeLookupRows<T extends { accountId: string }>(resolved: readonly { accountId: string }[], rows: readonly T[]): T[] {
  const seen = new Set(resolved.map((r) => r.accountId));
  return rows.filter((r) => !seen.has(r.accountId));
}

/** Option ids shared by the resolver rows and the table rows, for `aria-activedescendant`. */
export function rowId(accountId: string): string {
  return `lookup-row-${accountId}`;
}
